import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { defineRoute } from "../http/route.js";
import { ShopView } from "./listing.js";
/** The enterprise's shop (CAT-1, card 036). */
const Seller = { surface: "tenant", auth: "user", permission: "shop:manage" };
/** The longest texts a shop's profile takes, checked by the API and kept by the screens (card 042). */
export const SHOP_TEXT = { name: 120, description: 4000, qualificationTitle: 200 };
/** A shop's address, as it opens; reserved words and addresses taken are refused by the API as field issues. */
export const ShopSlug = z
    .string()
    .min(3)
    .max(40)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Lower-case letters, digits, and single hyphens");
const Qualification = z.object({ fileId: Id, title: LocalizedText });
const ServiceRegions = z.array(CountryCode).max(250);
export const openShop = defineRoute({
    ...Seller,
    id: "catalog.shop.open",
    method: "POST",
    path: "/v1/shop",
    marks: ["tenant", "verified", "idem"],
    summary: "Open the enterprise's shop, one per enterprise; the seller agreement in force must be accepted first. Its address is chosen here and changed only by operators",
    prd: ["CAT-1", "ENT-5"],
    request: {
        body: z.object({
            slug: ShopSlug,
            name: LocalizedText,
            description: LocalizedText.nullable().default(null),
            serviceRegions: ServiceRegions.default([]),
        }),
    },
    responses: { 201: { description: "The shop", schema: ShopView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "AGREEMENT_ACCEPTANCE_REQUIRED",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const getShop = defineRoute({
    ...Seller,
    id: "catalog.shop.get",
    method: "GET",
    path: "/v1/shop",
    marks: ["tenant"],
    summary: "The enterprise's shop: its profile, whether it is active, and whether it may sell",
    prd: ["CAT-1"],
    responses: { 200: { description: "The shop", schema: ShopView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getShopUsage = defineRoute({
    ...Seller,
    id: "catalog.shop.usage",
    method: "GET",
    path: "/v1/shop/usage",
    marks: ["tenant"],
    summary: "What the enterprise's shop uses of its plan: its live listings (ADR 0023, ENT-7); zero before a shop opens",
    prd: ["ENT-7"],
    responses: {
        200: { description: "Usage", schema: z.object({ liveListings: z.number().int().nonnegative() }) },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const updateShop = defineRoute({
    ...Seller,
    id: "catalog.shop.update",
    method: "PATCH",
    path: "/v1/shop",
    marks: ["tenant", "if-match"],
    summary: "Edit the profile. The shop becomes active once it has an English name and description and a service region, and an active shop keeps them",
    prd: ["CAT-1"],
    request: {
        body: z.object({
            name: LocalizedText.optional(),
            description: LocalizedText.nullable().optional(),
            logoFileId: Id.nullable().optional().describe("A clean `shop_logo` file uploaded for this shop"),
            qualifications: z
                .array(Qualification)
                .max(20)
                .optional()
                .describe("Clean `shop_qualification` files with titles; the files stay private, the titles are public"),
            serviceRegions: ServiceRegions.optional(),
        }),
    },
    responses: { 200: { description: "The shop", schema: ShopView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "VERSION_CONFLICT",
    ],
});
export const shopRoutes = [openShop, getShop, getShopUsage, updateShop];
//# sourceMappingURL=shop.routes.js.map